// Configuración del tablero (solo se ofrece a admins; el backend no lo distingue).
//
// · BoardEmpty: tablero sin columnas. Admin → plantilla sugerida que confirma y
//   se crea con POST /admin/deals/stages (una por etapa). Resto → aviso honesto.
// · ColumnsDialog: renombrar, color, probabilidad, orden, ocultar, volver a
//   mostrar y agregar columnas (PATCH / POST /admin/deals/stages; las ocultas
//   salen de GET ?include_inactive=1, solo admin). No hay borrado aquí: ocultar
//   (active=false) es reversible y no deja deals huérfanos.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, Check, Columns3, Eye, EyeOff, Loader2, Plus, Settings2, X } from 'lucide-react';

import { dealsApi, DealsApiError } from '../api';
import { KIND_LABEL, slugify, STAGE_COLORS, STAGE_TEMPLATE } from '../model';
import type { DealStage, DealStageCreateIn, DealStageKind } from '../types';
import { stageStyle } from './parts';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

export function BoardEmpty({ isAdmin, onReady, onCustomize, notify }: {
  isAdmin: boolean;
  onReady: () => void;
  onCustomize: () => void;
  notify: Notify;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Record<string, 'ok' | 'exists' | 'error'>>({});

  if (!isAdmin) {
    return (
      <div className="grid h-full place-items-center p-6">
        <div className="max-w-sm text-center">
          <span className="dl-accent-soft dl-accent-text mx-auto grid h-12 w-12 place-items-center rounded-2xl"><Columns3 className="h-6 w-6" aria-hidden /></span>
          <h2 className="mt-3 text-[16px] font-semibold text-foreground">El tablero aún no tiene columnas</h2>
          <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">
            Una persona administradora de tu organización tiene que configurar las etapas del pipeline. En cuanto lo haga, aquí verás tus deals.
          </p>
        </div>
      </div>
    );
  }

  const apply = async () => {
    setBusy(true);
    const res: Record<string, 'ok' | 'exists' | 'error'> = {};
    for (const s of STAGE_TEMPLATE) {
      try {
        await dealsApi.createStage(s);
        res[s.slug] = 'ok';
      } catch (e) {
        res[s.slug] = e instanceof DealsApiError && e.status === 409 ? 'exists' : 'error';
      }
      setDone({ ...res });
    }
    setBusy(false);
    const errors = Object.values(res).filter((v) => v === 'error').length;
    const exists = Object.values(res).filter((v) => v === 'exists').length;
    if (errors) notify('error', `No se crearon ${errors} columnas`, 'Revisa tu conexión o permisos e inténtalo de nuevo.');
    else notify('success', 'Tablero listo', exists ? `${exists} ya existían (ocultas); actívalas desde Columnas si las necesitas.` : 'Ya puedes crear deals.');
    onReady();
  };

  return (
    <div className="grid h-full place-items-center overflow-y-auto p-4 sm:p-6">
      <div className="w-full max-w-xl">
        <div className="text-center">
          <span className="dl-glyph mx-auto grid h-12 w-12 place-items-center rounded-2xl"><Columns3 className="h-6 w-6" aria-hidden /></span>
          <h2 className="mt-3 text-[18px] font-semibold text-foreground">Configura las columnas del pipeline</h2>
          <p className="mx-auto mt-1 max-w-md text-[13.5px] leading-relaxed text-muted-foreground">
            Cada columna es una etapa de venta. Te sugerimos esta plantilla; después puedes renombrar, cambiar colores u ocultar columnas.
          </p>
        </div>
        <ol className="mt-5 grid grid-cols-2 gap-2 min-[520px]:grid-cols-3" aria-label="Plantilla sugerida">
          {STAGE_TEMPLATE.map((s) => (
            <li key={s.slug} style={stageStyle({ color: s.color || '', kind: s.kind || 'open' })}
              className="dl-col-top flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <span className="dl-dot h-2.5 w-2.5 shrink-0 rounded-full" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-foreground">{s.name}</span>
                <span className="block text-[11.5px] text-muted-foreground">{s.kind === 'open' ? `${s.default_probability}% de cierre` : KIND_LABEL[s.kind || 'open']}</span>
              </span>
              {done[s.slug] === 'ok' && <Check className="h-4 w-4 dl-accent-text" aria-label="creada" />}
              {done[s.slug] === 'exists' && <span className="text-[11px] text-muted-foreground">ya existía</span>}
              {done[s.slug] === 'error' && <span className="text-[11px] text-destructive">falló</span>}
            </li>
          ))}
        </ol>
        <div className="mt-5 flex flex-col items-stretch gap-2 min-[420px]:flex-row min-[420px]:justify-center">
          {!confirm ? (
            <>
              <button type="button" onClick={() => setConfirm(true)} className="dl-btn-brand inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-5 text-[14px] font-semibold">
                <Check className="h-4 w-4" aria-hidden />Usar esta plantilla
              </button>
              <button type="button" onClick={onCustomize} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-4 text-[14px] font-medium text-foreground hover:bg-muted">
                <Settings2 className="h-4 w-4" aria-hidden />Armarla yo
              </button>
            </>
          ) : (
            <div role="group" aria-label="Confirmar plantilla" className="w-full rounded-xl border border-border bg-card p-3 text-center">
              <p className="text-[13.5px] text-foreground">Se crearán <strong>{STAGE_TEMPLATE.length} columnas</strong> para toda tu organización.</p>
              <div className="mt-3 flex flex-col-reverse gap-2 min-[420px]:flex-row min-[420px]:justify-center">
                <button type="button" onClick={() => setConfirm(false)} disabled={busy} className="h-11 rounded-xl px-4 text-[14px] font-medium text-foreground hover:bg-muted">Cancelar</button>
                <button type="button" onClick={apply} disabled={busy} autoFocus className="dl-btn-brand inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-5 text-[14px] font-semibold">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}Crear columnas
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Diálogo «Columnas» ────────────────────────────────────────

type Draft = { name: string; color: string; default_probability: string; kind: DealStageKind };

export function ColumnsDialog({ stages, onClose, onChanged, notify, portalTarget }: {
  stages: DealStage[];
  onClose: () => void;
  onChanged: () => void;
  notify: Notify;
  portalTarget?: Element | null;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [busyId, setBusyId] = useState<number | 'new' | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [add, setAdd] = useState<Draft>({ name: '', color: STAGE_COLORS[3], default_probability: '30', kind: 'open' });
  const [hidden, setHidden] = useState<DealStage[]>([]);

  // Ocultas: el backend solo las da a admins. Si falla (backend previo / 403),
  // la sección simplemente no aparece.
  const loadHidden = () => dealsApi.listStages(true)
    .then((all) => setHidden(all.filter((s) => !s.active)))
    .catch(() => setHidden([]));
  useEffect(() => { void loadHidden(); }, [stages]);

  useEffect(() => {
    setDrafts(Object.fromEntries(stages.map((s) => [s.id, { name: s.name, color: s.color || STAGE_COLORS[0], default_probability: String(s.default_probability), kind: s.kind }])));
  }, [stages]);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    boxRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [onClose]);

  const run = async (id: number | 'new', fn: () => Promise<unknown>, ok: string) => {
    setBusyId(id);
    try { await fn(); notify('success', ok); onChanged(); void loadHidden(); } catch (e: any) { notify('error', 'No se guardó', e?.message); } finally { setBusyId(null); }
  };

  const dirty = (s: DealStage) => {
    const d = drafts[s.id];
    return !!d && (d.name.trim() !== s.name || d.color !== s.color || Number(d.default_probability) !== s.default_probability || d.kind !== s.kind);
  };

  const swap = (i: number, j: number) => {
    const a = stages[i]; const b = stages[j];
    if (!a || !b) return;
    const ao = a.order === b.order ? a.order + (j > i ? 1 : -1) : b.order;
    void run(a.id, async () => { await dealsApi.updateStage(a.id, { order: ao }); await dealsApi.updateStage(b.id, { order: a.order }); }, 'Orden actualizado');
  };

  const swatches = (value: string, onPick: (c: string) => void, label: string) => (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {STAGE_COLORS.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} aria-label={c} onClick={() => onPick(c)}
          style={{ background: c }} className={`h-7 w-7 rounded-full ring-offset-2 ring-offset-card ${value === c ? 'ring-2 ring-foreground' : ''}`} />
      ))}
    </div>
  );

  return createPortal(
    <div className="deals-app fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={onClose}>
      <div ref={boxRef} role="dialog" aria-modal="true" aria-labelledby="dl-cols-title" data-dl-modal onMouseDown={(e) => e.stopPropagation()}
        className="dl-in flex max-h-[92dvh] w-full max-w-xl flex-col rounded-t-2xl border border-border bg-card text-card-foreground shadow-2xl sm:rounded-2xl">
        <header className="flex items-center gap-3 border-b border-border px-5 py-4">
          <span className="dl-accent-soft dl-accent-text grid h-9 w-9 place-items-center rounded-xl"><Columns3 className="h-5 w-5" aria-hidden /></span>
          <div className="min-w-0 flex-1">
            <h2 id="dl-cols-title" className="text-[16px] font-semibold">Columnas del pipeline</h2>
            <p className="text-[12.5px] text-muted-foreground">Cambia para toda la organización. Ocultar no borra deals.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="grid h-10 w-10 place-items-center rounded-xl text-muted-foreground hover:bg-muted"><X className="h-5 w-5" /></button>
        </header>
        <div className="dl-scroll min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {stages.map((s, i) => {
            const d = drafts[s.id];
            if (!d) return null;
            const set = (p: Partial<Draft>) => setDrafts((x) => ({ ...x, [s.id]: { ...x[s.id], ...p } }));
            return (
              <div key={s.id} style={stageStyle({ color: d.color, kind: d.kind })} className="dl-col-top rounded-xl border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <input className="dl-input min-w-0 flex-1" aria-label={`Nombre de la columna ${s.name}`} value={d.name} onChange={(e) => set({ name: e.target.value })} />
                  <IconBtn label="Subir" disabled={i === 0 || busyId !== null} onClick={() => swap(i, i - 1)}><ArrowUp className="h-4 w-4" /></IconBtn>
                  <IconBtn label="Bajar" disabled={i === stages.length - 1 || busyId !== null} onClick={() => swap(i, i + 1)}><ArrowDown className="h-4 w-4" /></IconBtn>
                  <IconBtn label={`Ocultar ${s.name}`} disabled={busyId !== null} onClick={() => run(s.id, () => dealsApi.updateStage(s.id, { active: false }), `«${s.name}» oculta`)}><EyeOff className="h-4 w-4" /></IconBtn>
                </div>
                <div className="mt-2 grid gap-2 min-[480px]:grid-cols-[1fr,auto,auto] min-[480px]:items-center">
                  {swatches(d.color, (c) => set({ color: c }), `Color de ${s.name}`)}
                  <label className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">Tipo
                    <select className="dl-input !w-auto !py-1.5" value={d.kind} onChange={(e) => set({ kind: e.target.value as DealStageKind })}>
                      {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">%
                    <input className="dl-input !w-16 !py-1.5 tabular-nums" type="number" min={0} max={100} value={d.default_probability} onChange={(e) => set({ default_probability: e.target.value })} aria-label={`Probabilidad de ${s.name}`} />
                  </label>
                </div>
                {dirty(s) && (
                  <div className="mt-2 flex justify-end">
                    <button type="button" disabled={busyId !== null || !d.name.trim()} className="dl-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-semibold"
                      onClick={() => run(s.id, () => dealsApi.updateStage(s.id, {
                        name: d.name.trim(), color: d.color, kind: d.kind,
                        default_probability: Math.max(0, Math.min(100, Math.round(Number(d.default_probability) || 0))),
                      }), 'Columna guardada')}>
                      {busyId === s.id && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}Guardar
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {hidden.length > 0 && (
            <div className="rounded-xl border border-border p-3">
              <p className="text-[13px] font-semibold text-foreground">Ocultas</p>
              <p className="mb-2 text-[12px] text-muted-foreground">No aparecen en el tablero. Muéstrala de nuevo cuando quieras.</p>
              <ul className="space-y-1.5">
                {hidden.map((s) => (
                  <li key={s.id} style={stageStyle(s)} className="flex items-center gap-2 rounded-lg bg-muted/40 px-2.5 py-1.5">
                    <span className="dl-dot h-2.5 w-2.5 shrink-0 rounded-full" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-[13.5px] text-muted-foreground">{s.name}</span>
                    <button type="button" disabled={busyId !== null}
                      onClick={() => run(s.id, () => dealsApi.updateStage(s.id, { active: true }), `«${s.name}» visible`)}
                      className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50">
                      {busyId === s.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}Mostrar
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-xl border border-dashed border-border p-3">
            <p className="mb-2 text-[13px] font-semibold text-foreground">Agregar columna</p>
            <div className="flex flex-col gap-2">
              <input className="dl-input" placeholder="Nombre (p. ej. Visita agendada)" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} aria-label="Nombre de la nueva columna" />
              <div className="grid gap-2 min-[480px]:grid-cols-[1fr,auto,auto] min-[480px]:items-center">
                {swatches(add.color, (c) => setAdd({ ...add, color: c }), 'Color de la nueva columna')}
                <select className="dl-input !w-auto !py-1.5" value={add.kind} onChange={(e) => setAdd({ ...add, kind: e.target.value as DealStageKind })} aria-label="Tipo">
                  {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input className="dl-input !w-16 !py-1.5 tabular-nums" type="number" min={0} max={100} value={add.default_probability} onChange={(e) => setAdd({ ...add, default_probability: e.target.value })} aria-label="Probabilidad %" />
              </div>
              <div className="flex justify-end">
                <button type="button" disabled={!add.name.trim() || busyId !== null}
                  onClick={() => {
                    const payload: DealStageCreateIn = {
                      slug: slugify(add.name), name: add.name.trim(), color: add.color, kind: add.kind,
                      default_probability: Math.max(0, Math.min(100, Math.round(Number(add.default_probability) || 0))),
                      order: (stages.reduce((m, s) => Math.max(m, s.order), 0) || 0) + 10,
                    };
                    void run('new', async () => { await dealsApi.createStage(payload); setAdd({ ...add, name: '' }); }, `«${payload.name}» agregada`);
                  }}
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13.5px] font-medium text-foreground hover:bg-muted disabled:opacity-50">
                  {busyId === 'new' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}Agregar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalTarget || document.body,
  );
}

function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40">
      {children}
    </button>
  );
}
